import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const meshGradientBackgroundSkill: Skill = {
    name: SKILL_NAMES.meshGradientBackground,
    kind: 'codeo',
    description:
        'Build a soft mesh of overlapping colour blooms behind the content and drift them slowly ' +
        'past each other — an ambient backdrop with more depth and colour than a flat fill.',
    instructions: `# Mesh Gradient Background

Several large, heavily blurred colour blooms overlap behind the content and drift slowly past one another. The colours mix where they meet, so the background is never flat and never quite the same twice — which gives a graphic somewhere to sit without giving the eye anything to read.

It is the richer sibling of a plain gradient wash. Reach for it when the palette has two or three colours worth showing and the content is a single strong element that needs a field behind it.

## Markup

Each bloom is one heavily blurred radial gradient. Three is usually enough; the blur is what turns hard circles into a mesh.

\`\`\`html
<div id="mesh" data-l5-allow-offscreen style="position: absolute; inset: -20%; overflow: hidden; background: var(--l5-color-1, #0b1020);">
  <div class="bloom" style="position: absolute; left: 6%; top: 10%; width: 900px; height: 900px; border-radius: 50%; filter: blur(180px); background: var(--l5-color-2, #38bdf8); opacity: 0.55;"></div>
  <div class="bloom" style="position: absolute; left: 52%; top: 34%; width: 1000px; height: 1000px; border-radius: 50%; filter: blur(200px); background: var(--l5-color-3, #7c5cff); opacity: 0.5;"></div>
  <div class="bloom" style="position: absolute; left: 26%; top: 58%; width: 780px; height: 780px; border-radius: 50%; filter: blur(170px); background: var(--l5-color-4, #f5a524); opacity: 0.36;"></div>
</div>
<div id="mesh-title" style="position: absolute; left: 0; right: 0; top: 460px; text-align: center; font-family: var(--l5-font-0), sans-serif; font-size: 128px; font-weight: 800; color: var(--l5-color-0, #ffffff);">Room to think</div>
\`\`\`

## Timeline code

Each bloom drifts on its own vector over the whole scene, so they never move as a block.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const SCENE = 8;
const blooms = gsap.utils.toArray('.bloom');
// Fixed per-bloom vectors: deterministic, and no two blooms travel together.
const DRIFTS = [
    { x: 140, y: -90 },
    { x: -120, y: 110 },
    { x: 90, y: 130 },
];

blooms.forEach((bloom, index) => {
    const drift = DRIFTS[index % DRIFTS.length];
    gsap.set(bloom, { x: -drift.x * 0.5, y: -drift.y * 0.5 });
    tl.to(bloom, { x: drift.x * 0.5, y: drift.y * 0.5, duration: SCENE, ease: 'none' }, 0);
});

tl.from('#mesh-title', { y: 26, opacity: 0, duration: 0.9, ease: 'power3.out' }, 0.3);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Blur radius**: 150–220px at 1920 wide. Below about 120px the individual circles become visible and the mesh reads as three dots.
- **Palette**: pull every bloom from \`--l5-color-N\` slots so the background follows the video's theme. A hand-picked colour here is the fastest way off-brand.
- **Contrast floor**: on a light template, drop the bloom opacities to 0.2–0.3 or the content loses contrast against the brightest patch.
- **Grain over the top**: a fine noise layer stops the smooth gradients from banding when rasterized — see \`${SKILL_NAMES.grainAndBloom}\`.
- **Slower and further**: doubling the drift distance and keeping the duration makes the field restless; halving it makes it nearly static, which suits a solemn close.
- **One bloom**: a single large wash anchored off one edge is the quiet version — see \`${SKILL_NAMES.atmosphereGradientField}\`.

## Pitfalls

- The container is inset past the frame so the blurred edges never enter it. That means it needs \`data-l5-allow-offscreen\`.
- Large blur radii are the most expensive thing in this library to rasterize. Three blooms is the practical ceiling; six will slow every frame of the render.
- Blooms are \`x\`/\`y\`-tweened, so none may carry a CSS \`transform\`; their layout position lives in \`left\`/\`top\`.
- Use \`ease: 'none'\` and span the whole scene, or the background settles and the frame dies with it.
- Never use \`Math.random()\` for the drift vectors — every frame must be a pure function of progress, so the vectors are fixed constants.
- Check the text's contrast at the *worst* moment of the drift, not just at the start.
- This is the scene's ambient layer. Don't also float the content and sweep a light across it.`,
};
